import imageCompression from 'browser-image-compression';
import axios from '@/axiosConfig';

export const CHUNK_SIZE = 950 * 1024;
export const PHOTO_UPLOAD_TIMEOUT = 180000;

export function looksLikeHeicNameOrMime(file) {
  const name = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  return (
    name.endsWith('.heic') ||
    name.endsWith('.heif') ||
    mime.includes('heic') ||
    mime.includes('heif')
  );
}

export async function looksLikeHeic(file) {
  if (looksLikeHeicNameOrMime(file)) return true;
  try {
    const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    if (header.length < 12) return false;
    const brand = String.fromCharCode(
      header[4],
      header[5],
      header[6],
      header[7],
    );
    const major = String.fromCharCode(
      header[8],
      header[9],
      header[10],
      header[11],
    ).toLowerCase();
    return (
      brand === 'ftyp' &&
      ['heic', 'heix', 'heif', 'mif1', 'msf1'].includes(major)
    );
  } catch {
    return false;
  }
}

export function isLikelyImageFile(file) {
  const mime = (file.type || '').toLowerCase();
  return (
    mime.startsWith('image/') ||
    mime === 'application/octet-stream' ||
    mime === '' ||
    /\.(heic|heif|jpe?g|png|gif|webp)$/i.test(file.name || '')
  );
}

export async function heicToJpegFile(file) {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.82),
    );
    if (!blob) return null;
    const fileName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg');
    return new File(
      [blob],
      fileName.endsWith('.jpg') ? fileName : `${fileName}.jpg`,
      { type: 'image/jpeg', lastModified: Date.now() },
    );
  } catch {
    return null;
  }
}

export async function prepareImageForWorkOrderUpload(fileToUpload) {
  let file = fileToUpload;
  let isHeic = await looksLikeHeic(file);
  if (isHeic) {
    const jpegFile = await heicToJpegFile(file);
    if (jpegFile) {
      file = jpegFile;
      isHeic = false;
    }
  }
  if (!isHeic) {
    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
      file = new File(
        [compressedFile],
        (file.name || 'photo').replace(/\.[^/.]+$/, '') + '.jpg',
        { type: 'image/jpeg', lastModified: Date.now() },
      );
    } catch (compressionError) {
      console.error('Error compressing image:', compressionError);
    }
  }
  return { file, isHeic };
}

export async function uploadFileLikeWorkOrder(file, isHeic, options) {
  const {
    singleUrl,
    fillSingleForm,
    initUrl,
    initBody = {},
    chunkUrl,
    finalizeUrl,
    skipGlobalError,
  } = options;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 15)}`;
  const timeout = PHOTO_UPLOAD_TIMEOUT;
  const requestConfig = { timeout };
  if (skipGlobalError) requestConfig.skipGlobalError = true;

  if (totalChunks === 1) {
    const formData = new FormData();
    formData.append('photo', file);
    if (fillSingleForm) fillSingleForm(formData);
    const response = await axios.post(singleUrl, formData, requestConfig);
    if (response.status !== 201) {
      throw new Error('Failed to upload photo');
    }
    return response;
  }

  const initResponse = await axios.post(
    initUrl,
    {
      fileName: file.name,
      fileType: file.type || (isHeic ? 'image/heic' : 'image/jpeg'),
      totalChunks,
      totalSize: file.size,
      uploadId,
      ...initBody,
    },
    requestConfig,
  );
  if (initResponse.status !== 200) {
    throw new Error('Failed to initialize chunked upload');
  }

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunkFormData = new FormData();
    chunkFormData.append('chunk', file.slice(start, end), 'chunk');
    chunkFormData.append('chunkIndex', String(chunkIndex));
    chunkFormData.append('uploadId', uploadId);
    const chunkResponse = await axios.post(
      chunkUrl,
      chunkFormData,
      requestConfig,
    );
    if (chunkResponse.status !== 200) {
      throw new Error(`Failed to upload chunk ${chunkIndex + 1}`);
    }
  }

  const finalizeResponse = await axios.post(
    finalizeUrl,
    { uploadId },
    requestConfig,
  );
  if (finalizeResponse.status !== 201) {
    throw new Error('Failed to finalize chunked upload');
  }
  return finalizeResponse;
}
